import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useApi } from '../../hooks/useApi';
import { errorText } from '../../pages/SoftwarePolicy/policyEdit';
import { cveUrl } from '../../pages/EndpointDevice/useEndpointSummary';
import { actionHint, programsOf, softwareFamilies } from '../../pages/EndpointDevice/topSoftware';
import { useV2Account } from '../useV2Account';
import CveActions from './CveActions';
import CveMachineActions from './CveMachineActions';
import { SortHeader, useSort } from './sortable';

const list = (v) => (Array.isArray(v) ? v : []);
const machineName = (item) => item.results?.computerName || item.deviceId;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// What one machine's result says about its software: the unique CVEs by severity, the worst score of its top programs, and how many
// programs with CVEs it has not used lately.
const machineRow = (item) => {
  const r = item.results || {};
  const top = programsOf(item);
  return {
    item,
    name: machineName(item),
    status: r['cve.status'],
    critical: num(r['cve.software.uniqueCvesBySeverity.CRITICAL']),
    high: num(r['cve.software.uniqueCvesBySeverity.HIGH']),
    cves: num(r['cve.software.uniqueCves']),
    worst: top.reduce((m, p) => Math.max(m, num(p.highestScore)), 0),
    unused: list(r['cve.unusedWithCves']).length,
    reported: item.updatedUtc || '',
    top,
  };
};

const APPLICATION_COLUMNS = {
  software: { get: (f) => f.family },
  versions: { get: (f) => f.versions.length },
  machines: { get: (f) => f.machines },
  critical: { get: (f) => f.critical },
  high: { get: (f) => f.high },
  score: { get: (f) => f.highestScore },
  action: { get: (f) => actionHint(f) },
};
const MACHINE_COLUMNS = {
  machine: { get: (m) => m.name },
  critical: { get: (m) => m.critical },
  high: { get: (m) => m.high },
  cves: { get: (m) => m.cves },
  score: { get: (m) => m.worst },
  unused: { get: (m) => m.unused },
  reported: { get: (m) => m.reported },
};

// Software vulnerabilities, V2: the known vulnerabilities of the software on an account's machines (Windows itself left out), by application
// (the programs with the worst vulnerabilities; one opens to the machines that have it) or by machine (each machine's numbers; one opens to
// its worst programs). Either table can be sorted by a column.
export default function CvePage() {
  const jApi = useApi();
  const apiRef = useRef(jApi);
  apiRef.current = jApi;
  const { accounts, account, choose, error: accountsError } = useV2Account();
  const [report, setReport] = useState({ state: 'idle' });
  const [open, setOpen] = useState(null);
  const [view, setView] = useState('application');
  const appSort = useSort();
  const machineSort = useSort();
  const code = account?.code;

  const load = useCallback(async () => {
    if (!code) return;
    setReport({ state: 'loading' });
    try {
      const response = await apiRef.current.request('GET', cveUrl(code, 'workstation'));
      setReport({ state: 'ok', body: response.data || {} });
    } catch (ex) {
      setReport({ state: 'error', error: errorText(ex) });
    }
  }, [code]);

  useEffect(() => { setOpen(null); load(); }, [load]);

  // The page sends the program and the machine ids; the API makes the jobs.
  const queueJobs = useCallback(async (url, body) => {
    const response = await apiRef.current.request('POST', url, body);
    return response.data;
  }, []);

  const choosePivot = (next) => { setView(next); setOpen(null); };

  const items = report.state === 'ok' ? list(report.body.items) : [];
  const families = appSort.apply(softwareFamilies(items), APPLICATION_COLUMNS);
  const machines = machineSort.apply(items.map(machineRow), MACHINE_COLUMNS);
  const empty = view === 'application' ? families.length === 0 : machines.length === 0;

  return (
    <div>
      <div className="v2-head">
        <div>
          <h1>Software vulnerabilities</h1>
          <p>The known vulnerabilities (CVEs) of the software installed on the machines of this account.</p>
        </div>
        <div>
          <select className="v2-select v2-account" aria-label="Account" value={code || ''} onChange={(e) => choose(e.target.value)}>
            {accounts.map((a) => <option key={a.code} value={a.code}>{a.name ? `${a.code} - ${a.name}` : a.code}</option>)}
          </select>
        </div>
      </div>

      {accountsError && <div className="v2-alert err">{accountsError}</div>}
      {report.state === 'error' && <div className="v2-alert err">{report.error}</div>}
      {report.state === 'ok' && report.body.dataComplete === false && <div className="v2-alert info">Not every machine result could be read, so some machines may be missing.</div>}

      <section className="v2-card">
        <div className="v2-bar">
          <div role="group" aria-label="View">
            <button type="button" className={`v2-btn${view === 'machine' ? ' primary' : ''}`} aria-pressed={view === 'machine'} onClick={() => choosePivot('machine')}>By machine</button>
            <button type="button" className={`v2-btn${view === 'application' ? ' primary' : ''}`} aria-pressed={view === 'application'} onClick={() => choosePivot('application')}>By application</button>
          </div>
          <p>
            {view === 'application'
              ? 'Programs with the worst known vulnerabilities, without Windows itself. Critical and high are the most on any one version. Choose a row to list its machines.'
              : 'Each machine with the vulnerabilities of its software, without Windows itself. Choose a row to list its worst programs.'}
          </p>
        </div>
        {report.state === 'loading' && <div className="v2-loading"><span className="v2-spin" role="status" aria-label="Loading" /></div>}
        {report.state === 'ok' && (empty ? <div className="v2-empty">{view === 'application' ? 'No known vulnerabilities in the software of this account.' : 'No machine of this account has reported its software yet.'}</div> : (
          view === 'application' ? (
            <table className="v2-table" aria-label="Top software vulnerabilities">
              <thead>
                <tr>
                  <th style={{ width: 30 }} />
                  <SortHeader k="software" sort={appSort.sort} onSort={appSort.toggle}>Software</SortHeader>
                  <SortHeader k="versions" sort={appSort.sort} onSort={appSort.toggle}>Versions</SortHeader>
                  <SortHeader k="machines" numeric sort={appSort.sort} onSort={appSort.toggle}>Machines</SortHeader>
                  <SortHeader k="critical" numeric sort={appSort.sort} onSort={appSort.toggle}>Critical</SortHeader>
                  <SortHeader k="high" numeric sort={appSort.sort} onSort={appSort.toggle}>High</SortHeader>
                  <SortHeader k="score" numeric sort={appSort.sort} onSort={appSort.toggle}>Worst score</SortHeader>
                  <SortHeader k="action" sort={appSort.sort} onSort={appSort.toggle}>Action</SortHeader>
                </tr>
              </thead>
              <tbody>
                {families.filter((f) => !open || open === f.key).map((f) => {
                  const isOpen = open === f.key;
                  const toggle = () => setOpen(isOpen ? null : f.key);
                  return (
                    <React.Fragment key={f.key}>
                      <tr className="row" aria-expanded={isOpen} tabIndex={0} onClick={toggle}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(); } }}>
                        <td><span className="v2-caret" aria-hidden="true">&#9656;</span></td>
                        <td style={{ fontWeight: 700 }}>{f.family}</td>
                        <td>{`${f.versions.length}: ${f.versions.join(', ')}`}</td>
                        <td className="num">{f.machines}</td>
                        <td className={`num${f.critical ? ' crit' : ''}`}>{f.critical}</td>
                        <td className={`num${f.high ? ' high' : ''}`}>{f.high}</td>
                        <td className="num">{f.highestScore}</td>
                        <td>{actionHint(f)}</td>
                      </tr>
                      {isOpen && (
                        <tr className="detail">
                          <td colSpan={8}>
                            <div className="v2-detail">
                              <div className="v2-detail-head"><h3>{`Machines with ${f.family}`}</h3></div>
                              <CveActions account={code} row={f} items={items} onQueue={queueJobs} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="v2-table" aria-label="Software vulnerabilities by machine">
              <thead>
                <tr>
                  <th style={{ width: 30 }} />
                  <SortHeader k="machine" sort={machineSort.sort} onSort={machineSort.toggle}>Machine</SortHeader>
                  <SortHeader k="critical" numeric sort={machineSort.sort} onSort={machineSort.toggle}>Critical</SortHeader>
                  <SortHeader k="high" numeric sort={machineSort.sort} onSort={machineSort.toggle}>High</SortHeader>
                  <SortHeader k="cves" numeric sort={machineSort.sort} onSort={machineSort.toggle}>CVEs</SortHeader>
                  <SortHeader k="score" numeric sort={machineSort.sort} onSort={machineSort.toggle}>Worst score</SortHeader>
                  <SortHeader k="unused" numeric sort={machineSort.sort} onSort={machineSort.toggle}>Unused, with CVEs</SortHeader>
                  <SortHeader k="reported" sort={machineSort.sort} onSort={machineSort.toggle}>Reported</SortHeader>
                </tr>
              </thead>
              <tbody>
                {machines.filter((m) => !open || open === m.item.deviceId).map((m) => {
                  const isOpen = open === m.item.deviceId;
                  const toggle = () => { if (m.top.length) setOpen(isOpen ? null : m.item.deviceId); };
                  return (
                    <React.Fragment key={m.item.deviceId}>
                      <tr className="row" aria-expanded={isOpen} tabIndex={0} onClick={toggle}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(); } }}>
                        <td><span className="v2-caret" aria-hidden="true">&#9656;</span></td>
                        <td style={{ fontWeight: 700 }}>
                          {m.name}
                          {m.status && m.status !== 'ok' && <div className="v2-sub">The vulnerability check did not complete for this machine.</div>}
                        </td>
                        <td className={`num${m.critical ? ' crit' : ''}`}>{m.critical}</td>
                        <td className={`num${m.high ? ' high' : ''}`}>{m.high}</td>
                        <td className="num">{m.cves}</td>
                        <td className="num">{m.worst}</td>
                        <td className="num">{m.unused}</td>
                        <td>{m.reported ? m.reported.slice(0, 16).replace('T', ' ') : ''}</td>
                      </tr>
                      {isOpen && (
                        <tr className="detail">
                          <td colSpan={8}>
                            <div className="v2-detail">
                              <div className="v2-detail-head"><h3>{`Worst programs on ${m.name}`}</h3></div>
                              <CveMachineActions account={code} item={m.item} programs={m.top} onQueue={queueJobs} />
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )
        ))}
      </section>
    </div>
  );
}
